/**
 * LocalStorageProvider — development storage provider.
 *
 * Uses browser Blob URLs for uploaded files and an in-memory
 * map for metadata. Always reports success so the upload
 * workflow can be exercised locally without Supabase Storage.
 */

import type {
  StorageProvider,
  StorageBucket,
  FileMetadata,
  ListOptions,
  ListResult,
  StoredFile,
} from './types';

interface StoredEntry {
  blob: Blob;
  path: string;
  name: string;
  mimeType: string;
  size: number;
  url: string;
  createdAt: string;
}

class LocalStorageProviderImpl implements StorageProvider {
  readonly name = 'local';
  private entries = new Map<string, StoredEntry>();

  async upload(
    _bucket: StorageBucket,
    path: string,
    body: Blob | ArrayBuffer,
    mimeType: string,
  ): Promise<{ path: string; url: string }> {
    const blob = body instanceof Blob ? body : new Blob([body], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const name = path.split('/').pop() ?? path;
    this.entries.set(path, {
      blob,
      path,
      name,
      mimeType,
      size: blob.size,
      url,
      createdAt: new Date().toISOString(),
    });
    return { path, url };
  }

  async downloadUrl(_bucket: StorageBucket, path: string): Promise<string> {
    const entry = this.entries.get(path);
    return entry?.url ?? '';
  }

  async delete(_bucket: StorageBucket, path: string): Promise<void> {
    const entry = this.entries.get(path);
    if (entry) {
      URL.revokeObjectURL(entry.url);
      this.entries.delete(path);
    }
  }

  async move(
    from: { bucket: StorageBucket; path: string },
    to: { bucket: StorageBucket; path: string },
  ): Promise<void> {
    const entry = this.entries.get(from.path);
    if (!entry) return;
    this.entries.delete(from.path);
    this.entries.set(to.path, { ...entry, path: to.path });
  }

  async copy(
    from: { bucket: StorageBucket; path: string },
    to: { bucket: StorageBucket; path: string },
  ): Promise<void> {
    const entry = this.entries.get(from.path);
    if (!entry) return;
    const url = URL.createObjectURL(entry.blob);
    this.entries.set(to.path, {
      ...entry,
      path: to.path,
      url,
      createdAt: new Date().toISOString(),
    });
  }

  async exists(_bucket: StorageBucket, path: string): Promise<boolean> {
    return this.entries.has(path);
  }

  async metadata(_bucket: StorageBucket, path: string): Promise<FileMetadata | null> {
    const entry = this.entries.get(path);
    if (!entry) return null;
    return {
      name: entry.name,
      size: entry.size,
      mimeType: entry.mimeType,
      lastModified: entry.createdAt,
    };
  }

  async list(_bucket: StorageBucket, options?: ListOptions): Promise<ListResult> {
    const limit = options?.limit ?? 50;
    const offset = options?.offset ?? 0;
    const all = Array.from(this.entries.values());
    const files: StoredFile[] = all.slice(offset, offset + limit).map((e) => ({
      id: e.path,
      bucket: _bucket,
      path: e.path,
      name: e.name,
      originalName: e.name,
      mimeType: e.mimeType,
      size: e.size,
      url: e.url,
      uploadedAt: e.createdAt,
      accessLevel: 'public',
      category: 'attachment',
    }));
    return {
      files,
      total: all.length,
      hasMore: offset + limit < all.length,
    };
  }

  async isAvailable(): Promise<boolean> {
    return true;
  }
}

export const LocalStorageProvider: StorageProvider = new LocalStorageProviderImpl();
